import { RegisteredModel } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_pb';
import { GetRegisteredModelResponse } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_service_pb';
import { RegisteredModelVersion } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_pb';
import {
  GetModelVersionForExperimentRequest,
  GetModelVersionForExperimentResponse,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_service_pb';
import { RegisteredModelService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_service_connect';
import { RegisteredModelVersionService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_version_service_connect';
import type { PlainMessage } from '@bufbuild/protobuf';
import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import {
  type IColumn,
  type IContextualMenuItem,
  type IDropdownOption,
  type IStyle,
  MessageBarType,
  Toggle,
} from '@fluentui/react';
import {
  Button,
  buttonStylesPrimary,
  buttonStylesStealth,
  Dropdown,
  IH2OTheme,
  Info,
  ListRow,
  Loader,
  TextField,
  useClassNames,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import { isEqual } from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';
import semver from 'semver';

import { useUsers } from '../../authz/providers/UsersProvider';
import {
  ButtonFailed,
  ButtonPaused,
  ButtonPausing,
  ButtonReady,
  ButtonStarting,
} from '../../components/ButtonStartPause/ButtonStartPause';
import { FALLBACK_MLOPS_API_VERSION, useQueryMlopsApiVersion } from '../../discovery-service/discoveryService.hooks';
import { DeploymentFixed } from '../../mlops/api';
import { SecurityOptionType } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/config_pb';
import { DeploymentComposition } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_composition_pb';
import { deploymentName as deploymentNameCompileParse } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_pb';
import { ShadowElement, SplitElement } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_pb';
import {
  CreateDeploymentRequest,
  UpdateDeploymentRequest,
} from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_service_pb';
import {
  DeploymentStatus,
  DeploymentStatus_DeploymentState,
} from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_status_pb';
import { KubernetesResourceSpec } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/kubernetes_resource_spec_pb';
import {
  AuthenticationPassphrase_HashAlgorithm,
  AuthorizationAccessToken_AuthorizationProtocolSecurityType,
} from '../../mlops/gen/ai/h2o/mlops/deployer/v2/security_pb';
import { type BytesString, bytesStringToUint8Array } from '../../mlops/gen/runtime';
import { useMLOpsService } from '../../mlops/hooks';
import {
  useMutationDeleteDeployment,
  useMutationRetryDeployment,
  usePauseResumeDeployment,
} from '../../mlops/mlops.deployer.hooks';
import { useQueryBaselineAggregates } from '../../mlops/mlops.monitoring.hooks';
import type { SecurityKey } from '../../mlops/utils';
import { useCloudPlatformDiscovery } from '../../utils/hooks';
import { ClassNamesFromIStyles } from '../../utils/models';
import { formatError } from '../../utils/utils';
import { OneOf } from '../Orchestrator/WorkflowTabCanvas';
import { getRandomAdjective } from './adjectives';
import { FORM_SECTION_MAX_WIDTH, FormSection } from './BatchScoringDetail';
import { useConfirmDialog } from './ConfirmDialogProvider';
import { ENDPOINTS, ROUTES } from './constants';
import { type DeploymentItem, DeploymentStatusWidget } from './Deployments';
import { DeploymentOptions, ListItemDescription } from './Models';
import ModelSelector, { Expander } from './ModelSelector';
import Monitoring, { BaselineAggregationFixed, COLUMN_UPDATE_SUPPORTED, MonitoringOptionsFixed } from './Monitoring';
import { type GranularMLOpsPermissions, useGranularMLOpsPermissions } from './userActions';

export interface IDeploymentTabDetailProps {
  deployment?: DeploymentFixed;
  deploymentStatus?: DeploymentStatus;
  defaultSelectedSecurityOptionKey?: SecurityKey;
  deployModelVersionId?: string;
  isNew: boolean;
  updateDeleteAllowed: boolean;
  isMonitoringEnabledGlobally?: boolean;
  workspaceId: string;
}

export type DeploymentTypeKey = 'single_model' | 'ab_test' | 'champion_challenger';
export type DropdownOption = { key: string | number; text: string; data?: any };

type SecurityOptionTypeFixed = SecurityOptionType &
  OneOf<{
    disableSecurityType?: {} | null;
    hashAlgorithm?: AuthenticationPassphrase_HashAlgorithm;
    authorizationProtocol?: AuthorizationAccessToken_AuthorizationProtocolSecurityType;
  }>;

interface IDeploymentTabDetailStyles {
  form: IStyle;
  metadata: IStyle;
}

type DeploymentItemData = DeploymentFixed & {
  actionsDisabled: boolean;
  createdByName: string;
  id: string;
  onDeleteDeployment: () => void;
  onRetryDeployment: () => void;
  permissions: GranularMLOpsPermissions;
  status: DeploymentStatus;
  workspaceId: string;
};

const DEFAULT_TRAFFIC_WEIGHT = 50;
const MAX_TRAFFIC_WIGHT = 100;

export const getIdFromName = (name?: string) => {
  if (!name) return '';
  const parts = name.split('/');
  return parts[parts.length - 1];
};

export const countSelectedModels = (selectedModels: (PlainMessage<RegisteredModel> | undefined)[]) => {
  return selectedModels.filter((model) => model !== undefined).length;
};

export const getDeploymentType = (item: DeploymentFixed) => {
  if (item?.single) return 'single_model';
  if (item?.shadow) return 'champion_challenger';
  if (item?.split) return 'ab_test';
  return 'single_model';
};

export const concatByteArrays = (...arrays: Uint8Array[]): Uint8Array => {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    result.set(arr, offset);
    offset += arr.length;
  }
  return result;
};

export const downloadFileFromGoogleByteStream = async (
  resourceName: string,
  baseUrl: string,
  fileName: string
): Promise<void> => {
  const url = new URL(`${baseUrl}/storage/google.bytestream.ByteStream/Read`);
  const requestBody = JSON.stringify({ resourceName });

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: requestBody,
  });

  if (!response.ok) {
    throw new Error(`Download failed: ${response.status} ${await response.text()}`);
  }

  // The response contains newline-delimited JSON (NDJSON). We cannot use `await response.json()` as that would only
  // parse the first JSON object - the first line. The NDJSON format is used by the server to _stream_ data and the
  // data can be also processed in a streaming fashion. @see https://davidwalsh.name/streaming-data-fetch-ndjson
  // The trouble here is, that we need to construct a Blob from the _complete_ data, therefor streaming processing
  // would not gain any performance benefits, because we need to load all data into memory anyway.
  // If the "load into memory" will prove to be a real problem (payload would be hundreds of MBs or GBs), we could use
  // the code from Doc-AI where it handles streaming download of potentially very large artifacts.
  // @see https://github.com/h2oai/doc-ai/blob/97c41783eba6de9fe4c14670a346dba44f3ae63e/react-new/src/api/authorizedDownload.ts
  // The Doc-AI code is heavily inspired by the https://github.com/jimmywarting/StreamSaver.js library and the author
  // of that library is involved in the development of the standard File System API, which was not stable at the time
  // of writing of the Doc-AI code, but might be good option to use now.
  // @see https://developer.mozilla.org/en-US/docs/Web/API/File_System_API
  // Here we read the whole response as a single large string of multiple, newline-delimited JSON objects and then we
  // process it line by line.
  const text = await response.text();

  const binaryChunks: Uint8Array[] = [];

  // Parse each line as JSON object, extract data, and decode it from Base64 into byte-arrays
  text
    .trim()
    .split('\n')
    .forEach((line) => {
      const parsed = JSON.parse(line) as { result?: { data: BytesString } };
      const byteString = parsed?.result?.data;
      if (byteString) {
        binaryChunks.push(bytesStringToUint8Array(byteString));
      }
    });

  const combined = concatByteArrays(...binaryChunks);
  const blob = new Blob([combined], { type: 'application/zip' });

  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
};

const deploymentTabDetailStyles = (theme: IH2OTheme): IDeploymentTabDetailStyles => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: 20,
    margin: '0px 10px',
    // TODO: Remove once theme is used.
    borderColor: theme.semanticColors?.bodyBackground,
  },
  metadata: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
});

const useListRowColumns = ({
    onDeleteDeployment,
    onPauseDeployment,
    onRetryDeployment,
    onResumeDeployment,
    permissions,
    isMlopsApiV1_0_x,
  }: {
    onDeleteDeployment: (deploymentName: string) => void;
    onPauseDeployment: (deployment: DeploymentFixed) => void;
    onRetryDeployment: (deploymentName: string) => void;
    onResumeDeployment: (deployment: DeploymentFixed) => void;
    permissions: GranularMLOpsPermissions;
    isMlopsApiV1_0_x: boolean;
  }): IColumn[] => [
    {
      key: 'status',
      fieldName: 'status',
      maxWidth: 200,
      minWidth: 140,
      name: 'Status',
      data: {
        listCellProps: {
          onRenderText: ({ status, workspaceId, id }: DeploymentItem) => (
            <DeploymentStatusWidget
              deploymentId={id}
              initialStatus={status}
              showTooltip={false}
              workspaceId={workspaceId}
            />
          ),
          onRenderHeader: () => <></>,
        },
      },
    },
    {
      key: 'title',
      name: 'Title',
      minWidth: 480,
      maxWidth: 480,
      data: {
        headerFieldName: 'displayName',
        listCellProps: {
          onRenderHeader: () => <></>,
          onRenderText: ({ createdByName, createTime, updateTime }: DeploymentItemData) => (
            <>
              <ListItemDescription
                createdBy={createdByName}
                createdAt={createTime || ``}
                modifiedAt={updateTime || ``}
              />
              <br />
            </>
          ),
        },
      },
    },
    {
      key: 'buttons',
      name: '',
      maxWidth: 360,
      minWidth: 300,
      data: {
        listCellProps: {
          onRenderText: isMlopsApiV1_0_x
            ? ({ name, actionsDisabled, status }: DeploymentItem) => (
                <>
                  {permissions.isAbleToDeleteDeployments || permissions.isAbleToRetryDeployments ? (
                    <>
                      {status.state === DeploymentStatus_DeploymentState.FAILED &&
                      permissions.isAbleToRetryDeployments ? (
                        <Button
                          iconName="Refresh"
                          text="Retry"
                          onClick={() => onRetryDeployment(name)}
                          style={{ marginRight: 8 }}
                        />
                      ) : null}
                      {permissions.isAbleToDeleteDeployments ? (
                        <Button
                          text="Delete"
                          onClick={() => onDeleteDeployment(name)}
                          style={{
                            color: 'var(--h2o-red400)',
                            borderColor: 'var(--h2o-red400)',
                          }}
                          disabled={actionsDisabled}
                          iconProps={{ iconName: 'Delete', style: { color: 'var(--h2o-red400)' } }}
                        />
                      ) : null}
                    </>
                  ) : null}
                </>
              )
            : (item: DeploymentItem) => {
                const {
                  actionsDisabled,
                  name,
                  status: { state },
                } = item;
                const menuItems = [
                  permissions.isAbleToDeleteDeployments && {
                    disabled:
                      actionsDisabled ||
                      (isMlopsApiV1_0_x
                        ? !(
                            item.status?.state === DeploymentStatus_DeploymentState.HEALTHY ||
                            item.status?.state === DeploymentStatus_DeploymentState.FAILED ||
                            item.status?.state === DeploymentStatus_DeploymentState.UNHEALTHY ||
                            item.status?.state === DeploymentStatus_DeploymentState.STOPPED
                          )
                        : !item.status.updatesAllowed),
                    key: 'deleteDeployment',
                    text: 'Delete deployment',
                    onClick: () => onDeleteDeployment(name),
                    style: { color: 'var(--h2o-red400)' },
                    iconProps: { iconName: 'Delete', style: { color: 'var(--h2o-red400)' } },
                  },
                ].filter(Boolean) as IContextualMenuItem[];
                switch (state) {
                  case DeploymentStatus_DeploymentState.LAUNCHING:
                  case DeploymentStatus_DeploymentState.PREPARING:
                  case DeploymentStatus_DeploymentState.DEPLOYED:
                    return <ButtonStarting menuItems={menuItems} />;
                  case DeploymentStatus_DeploymentState.FAILED:
                    return (
                      <ButtonFailed
                        menuItems={menuItems}
                        onClick={() => onRetryDeployment(name)}
                        primaryDisabled={!permissions.canRetryDeployments}
                      />
                    );
                  case undefined:
                  case DeploymentStatus_DeploymentState.DEPLOYMENT_STATE_UNSPECIFIED:
                  case DeploymentStatus_DeploymentState.HEALTHY:
                  case DeploymentStatus_DeploymentState.UNHEALTHY:
                    return (
                      <ButtonReady
                        menuItems={menuItems}
                        onClick={() => {
                          onPauseDeployment(item);
                        }}
                        primaryDisabled={!permissions.isAbleToPauseDeployments}
                      />
                    );
                  case DeploymentStatus_DeploymentState.PENDING:
                    return <ButtonPausing menuItems={menuItems} text="Pending" />;
                  case DeploymentStatus_DeploymentState.STOPPED:
                    return (
                      <ButtonPaused
                        menuItems={menuItems}
                        onClick={() => {
                          onResumeDeployment(item);
                        }}
                        primaryDisabled={!permissions.isAbleToResumeDeployments}
                      />
                    );
                  case DeploymentStatus_DeploymentState.TERMINATING:
                    return <ButtonPausing menuItems={menuItems} />;
                  case DeploymentStatus_DeploymentState.TERMINATED:
                    return <ButtonPaused menuItems={menuItems} primaryDisabled={true} />;
                  default:
                    // exhaustive switch-case check
                    const s: never = state;
                    throw new Error(`Unknown deployment state: ${s}`);
                }
              },
          styles: {
            root: {
              display: 'flex',
              flexGrow: 1,
              justifyContent: 'end',
              marginRight: 9,
            },
          },
        },
      },
    },
  ],
  getListRowStyles = (theme: IH2OTheme) => ({
    details: {
      padding: '0px 20px',
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
    },
    root: {
      borderRadius: 4,
      backgroundColor: theme.semanticColors?.contentBackground,
    },
    contents: {
      paddingTop: 4,
      paddingBottom: 16,
    },
    row: {
      '&:hover': {
        background: 'transparent',
      },
      '.h2o-ListCell-root:first-child': {
        paddingLeft: 20,
      },
      height: 80,
    },
  }),
  dropdownOptions: DropdownOption[] = [
    { key: 'single_model', text: 'Single model' },
    { key: 'ab_test', text: 'A/B Test' },
    { key: 'champion_challenger', text: 'Champion/Challenger' },
  ],
  getSecurityDropdownOptions = (
    securityOptionTypes: SecurityOptionTypeFixed[],
    defaultSelectedSecurityOptionKey?: string
  ) => {
    const newSecOptions: IDropdownOption[] = securityOptionTypes.map((option) => {
      return {
        key: (option?.hashAlgorithm || option?.authorizationProtocol || 'NO_SECURITY') as SecurityKey,
        text: option.displayName || ``,
        data: option,
      };
    });
    // If defaultSelectedSecurityOptionKey for existing deployment is 'BCRYPT', we want to keep it for backward compatibility. Otherwise we don't want to offer user this option.
    if (defaultSelectedSecurityOptionKey === AuthenticationPassphrase_HashAlgorithm.BCRYPT) {
      return newSecOptions;
    } else {
      return newSecOptions.filter((option) => option.key !== AuthenticationPassphrase_HashAlgorithm.BCRYPT);
    }
  },
  getRandomHash = () =>
    new Array(20)
      .fill('0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz')
      .map((x) =>
        (function (chars) {
          const umax = Math.pow(2, 32),
            r = new Uint32Array(1),
            max = umax - (umax % chars.length);
          do {
            crypto.getRandomValues(r);
          } while (r[0] > max);
          return chars[r[0] % chars.length];
        })(x)
      )
      .join('');

export const capitalize = <T extends string>(text: T) =>
  text ? ((text[0].toUpperCase() + text.slice(1)) as Capitalize<typeof text>) : text;

export const DeploymentTabDetail = ({
  deployment: defaultDeployment,
  deploymentStatus,
  deployModelVersionId,
  defaultSelectedSecurityOptionKey,
  isNew,
  updateDeleteAllowed,
  isMonitoringEnabledGlobally,
  workspaceId,
}: IDeploymentTabDetailProps) => {
  const { addToast } = useToast();
  const { showDialog } = useConfirmDialog();
  const workspaceName = React.useMemo(() => `workspaces/${workspaceId}`, [workspaceId]);
  const deleteDeploymentMutation = useMutationDeleteDeployment({
    onError: (err) => {
      const message = `Failed to delete deployment: ${formatError(err)}`;
      console.error(message);
      addToast({
        messageBarType: MessageBarType.error,
        message,
      });
    },
    onSuccess: () => {
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Deployment deleted successfully.',
      });
    },
  });

  const handleDeleteDeployment = (deploymentName: string) => {
    showDialog?.({
      title: 'Delete deployment?',
      onConfirm: () => {
        setWidgetRowActionsDisabled(true);
        deleteDeploymentMutation.mutate(
          { name: deploymentName },
          {
            onSuccess: () => {
              setWidgetRowActionsDisabled(false);
              history.push(`/mlops/${workspaceName}${ROUTES.DEPLOYMENTS}`);
            },
          }
        );
      },
    });
  };

  const theme = useTheme(),
    classNames = useClassNames<IDeploymentTabDetailStyles, ClassNamesFromIStyles<IDeploymentTabDetailStyles>>(
      'deploymentTabDetail',
      deploymentTabDetailStyles(theme)
    ),
    history = useHistory(),
    // Deployments comes without baselineAggregations by default as these can be large. We fetch them from separate service.
    [deployment, setDeployment] = React.useState<DeploymentFixed | undefined>(defaultDeployment),
    { usersNameMap } = useUsers(),
    // Handles support for old monitoring where baselineAggregations was not mandatory and are coming as empty array from database.
    isEmptyBaselineMonitoring = React.useMemo(() => {
      return (
        deployment?.monitoringOptions?.baselineAggregations &&
        deployment?.monitoringOptions?.baselineAggregations.length === 0
      );
    }, [deployment?.monitoringOptions?.baselineAggregations]),
    permissions = useGranularMLOpsPermissions(workspaceName),
    mlopsService = useMLOpsService(),
    [deploymentType, setDeploymentType] = React.useState<DeploymentTypeKey>(
      dropdownOptions[0].key as DeploymentTypeKey
    ),
    // TODO: Move to context global to mlops.
    cloudPlatformDiscovery = useCloudPlatformDiscovery(),
    mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '',
    storageTransport = createConnectTransport({
      baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
    }),
    registeredModelClient = createClient(RegisteredModelService, storageTransport),
    registeredModelVersionClient = createClient(RegisteredModelVersionService, storageTransport),
    [selectedModels, setSelectedModels] = React.useState<(PlainMessage<RegisteredModel> | undefined)[]>([]),
    areAllModelsSelected = React.useMemo(
      () =>
        (deploymentType === 'single_model' && countSelectedModels(selectedModels) > 0) ||
        (deploymentType !== 'single_model' && countSelectedModels(selectedModels) > 1),
      [deploymentType, selectedModels]
    ),
    [defaultDeploymentCompositionA, setDefaultDeploymentCompositionA] = React.useState<
      DeploymentComposition | undefined
    >(),
    [defaultDeploymentCompositionB, setDefaultDeploymentCompositionB] = React.useState<
      DeploymentComposition | undefined
    >(),
    [defaultSelectedModelIds, setDefaultSelectedModelIds] = React.useState<string[]>(),
    [defaultSelectedExperimentIds, setDefaultSelectedExperimentIds] = React.useState<string[]>(),
    [trafficDistribution, setTrafficDistribution] = React.useState<number>(DEFAULT_TRAFFIC_WEIGHT),
    [isWidgetLoading, setIsWidgetLoading] = React.useState(true),
    [isMonitoringEnabled, setIsMonitoringEnabled] = React.useState<boolean>(false),
    [monitoringOptions, setMonitoringOptions] = React.useState<MonitoringOptionsFixed | undefined>(undefined),
    [nonUpdatableColumns, setNonUpdatableColumns] = React.useState<string[]>(),
    [deploymentOptions, setDeploymentOptions] = React.useState<DeploymentOptions | undefined>(undefined),
    [deploymentName, setDeploymentName] = React.useState<string>(
      isNew ? `${capitalize(getRandomAdjective())} deployment` : ''
    ),
    [deploymentDescription, setDeploymentDescription] = React.useState<string>(''),
    [passphrase, setPassphrase] = React.useState(() => getRandomHash()),
    [selectedSecurityOptionKey, setSelectedSecurityOptionKey] = React.useState<SecurityKey | undefined>(),
    [selectedSecurityOption, setSelectedSecurityOption] = React.useState<SecurityOptionTypeFixed | undefined>(
      undefined
    ),
    hasAuthProtocol = !!selectedSecurityOption?.authorizationProtocol,
    hasHashAlgorithm = !!selectedSecurityOption?.hashAlgorithm,
    isPlaintext = selectedSecurityOption?.hashAlgorithm === 'PLAINTEXT',
    isHashed =
      selectedSecurityOption?.hashAlgorithm === AuthenticationPassphrase_HashAlgorithm.PBKDF2 ||
      selectedSecurityOption?.hashAlgorithm === AuthenticationPassphrase_HashAlgorithm.BCRYPT,
    [hasPassphraseChanged, setHasPassphraseChanged] = React.useState(false),
    isHashUnchanged = !isNew && isHashed && !hasPassphraseChanged,
    showHashInput = hasHashAlgorithm && !isHashUnchanged,
    [securityOptions, setSecurityOptions] = React.useState<SecurityOptionTypeFixed[]>([]),
    [securityDropdownOptions, setSecurityDropdownOptions] = React.useState<IDropdownOption[]>([]),
    [defaultKubernetesResourceSpec, setDefaultKubernetesResourceSpec] = React.useState<KubernetesResourceSpec>(),
    [defaultEnvVariablesConfig, setDefaultEnvVariablesConfig] = React.useState<Record<string, string>>(),
    [affinityFromSelector, setAffinityFromSelector] = React.useState<string>('none'),
    [tolerationFromSelector, setTolerationFromSelector] = React.useState<string>('none'),
    // TODO: Handle loading.
    [, setLoading] = React.useState(true),
    [isGeneratingLogs, setIsGeneratingLogs] = React.useState(false),
    [isSubmitDisabled, setIsSubmitDisabled] = React.useState(false),
    [widgetRowActionsDisabled, setWidgetRowActionsDisabled] = React.useState(false),
    [isAdvancedMonitoringExpanded, setAdvancedMonitoringExpanded] = React.useState(false),
    [noMonitoringColumnsSpecified, setNoMonitoringColumnsSpecified] = React.useState(true),
    [updateMask, setUpdateMask] = React.useState<string>(),
    experimentId = React.useMemo(() => {
      return (
        deploymentOptions?.firstModelDeploymentComposition?.experimentId ||
        deploymentOptions?.secondModelDeploymentComposition?.experimentId ||
        defaultDeploymentCompositionA?.experimentId ||
        defaultDeploymentCompositionB?.experimentId
      );
    }, [
      deploymentOptions?.firstModelDeploymentComposition?.experimentId,
      deploymentOptions?.secondModelDeploymentComposition?.experimentId,
      defaultDeploymentCompositionA?.experimentId,
      defaultDeploymentCompositionB?.experimentId,
    ]),
    isExistingMonitoring = React.useMemo(
      () => Boolean(!isNew && defaultDeployment?.monitoringOptions !== null && experimentId),
      [defaultDeployment?.monitoringOptions, experimentId, isNew]
    ),
    loadStateRef = React.useRef({
      createDeployment: false,
      updateDeployment: false,
      fetchingExistingDeploymentDetails: false,
      deletingDeployment: false,
      fetchingDeployerLogs: false,
      fetchingKubernetesConfigurationShortcuts: false,
      fetchingSecurityOptions: false,
      fetchingDefaultSecurityOption: false,
    }),
    evaluateLoading = () => {
      if (
        !loadStateRef.current.createDeployment &&
        !loadStateRef.current.updateDeployment &&
        !loadStateRef.current.fetchingExistingDeploymentDetails &&
        !loadStateRef.current.deletingDeployment &&
        !loadStateRef.current.fetchingDeployerLogs &&
        !loadStateRef.current.fetchingKubernetesConfigurationShortcuts &&
        !loadStateRef.current.fetchingSecurityOptions &&
        !loadStateRef.current.fetchingDefaultSecurityOption
      ) {
        setLoading(false);
      }
    },
    baselineAggregatesQuery = useQueryBaselineAggregates(
      {
        deploymentId: getIdFromName(defaultDeployment?.name) || '',
        experimentId: experimentId || '',
        pageSize: 20,
        workspaceId,
      },
      {
        enabled: isExistingMonitoring,
        meta: {
          toastError: (err: unknown) => `Failed to fetch baseline aggregates: ${formatError(err)}`,
        },
        select: (data) => {
          const flattenedBaselineAggregates = data.pages.flatMap((page) => page.baselineAggregates || []);
          return { pages: flattenedBaselineAggregates, pageParams: data.pageParams };
        },
      }
    ),
    baselineAggregates = baselineAggregatesQuery.data?.pages,
    onChangeDeploymentType = React.useCallback((_event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
      if (item) {
        setDeploymentType(item.key as DeploymentTypeKey);
        setTrafficDistribution(50);
        setSelectedModels((selectedModels) => (item.key === 'single_model' ? [selectedModels?.[0]] : selectedModels));
      }
    }, []),
    onChangeTrafficDistribution = (value?: number) => {
      if (value) setTrafficDistribution(Number(value));
    },
    deploymentDetailProps = React.useMemo(
      () => ({
        createdBy: usersNameMap?.[deployment?.creator || '']?.displayName || deployment?.creator,
      }),
      [deployment, usersNameMap]
    ),
    retryDeploymentMutation = useMutationRetryDeployment({
      onError: (err) => {
        addToast({
          messageBarType: MessageBarType.error,
          message: `Failed to retry deployment: ${formatError(err)}`,
        });
        setWidgetRowActionsDisabled(false);
      },
      onSuccess: () => {
        setWidgetRowActionsDisabled(false);
        history.push(`/mlops/${workspaceName}${ROUTES.DEPLOYMENTS}`);
      },
    }),
    getSecurityOptions = React.useCallback(async () => {
      loadStateRef.current.fetchingSecurityOptions = true;
      setLoading(true);
      try {
        const response = await mlopsService.listSecurityOptionTypes({});
        const securityOptionTypes = (response?.securityOptionTypes || []) as SecurityOptionTypeFixed[];
        setSecurityOptions(securityOptionTypes);
      } catch (err) {
        const message = `Failed to fetch security options: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      } finally {
        loadStateRef.current.fetchingSecurityOptions = false;
        evaluateLoading();
      }
    }, []),
    getDefaultSecurityOption = React.useCallback(async () => {
      loadStateRef.current.fetchingDefaultSecurityOption = true;
      setLoading(true);
      try {
        const response = await mlopsService.getDefaultSecurityOptionType({});
        const defaultSecurityOption = response?.defaultSecurityOptionType as SecurityOptionTypeFixed | undefined;
        setSelectedSecurityOptionKey(
          defaultSecurityOption?.hashAlgorithm || defaultSecurityOption?.authorizationProtocol || 'NO_SECURITY'
        );
        setSelectedSecurityOption(defaultSecurityOption);
      } catch (err) {
        const message = `Failed to fetch default security option: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      } finally {
        loadStateRef.current.fetchingDefaultSecurityOption = false;
        evaluateLoading();
      }
    }, []),
    createDeployment = React.useCallback(async () => {
      if (!workspaceName || !deploymentOptions) {
        return;
      }
      loadStateRef.current.createDeployment = true;
      setLoading(true);
      setIsSubmitDisabled(true);
      try {
        const primaryElement = {
          deploymentComposition: deploymentOptions.firstModelDeploymentComposition,
          weight: deploymentType === 'ab_test' ? trafficDistribution : undefined,
          kubernetesResourceSpec: {
            kubernetesResourceRequirements: {
              requests: deploymentOptions.requests,
              limits: deploymentOptions.limits,
            },
            replicas: deploymentOptions.kubernetesOptions.replicas,
          },
          kubernetesConfigShortcut: {
            kubernetesAffinityShortcut: affinityFromSelector === 'none' ? '' : affinityFromSelector || '',
            kubernetesTolerationShortcut: tolerationFromSelector === 'none' ? '' : tolerationFromSelector || '',
          },
          runtimeEnvironmentVariables: deploymentOptions.envVariables,
        };

        const secondaryElement = {
          deploymentComposition: deploymentOptions.secondModelDeploymentComposition,
          weight: deploymentType === 'ab_test' ? MAX_TRAFFIC_WIGHT - trafficDistribution : undefined,
          kubernetesResourceSpec: {
            kubernetesResourceRequirements: {
              requests: deploymentOptions.requests,
              limits: deploymentOptions.limits,
            },
            replicas: deploymentOptions.kubernetesOptions.replicas,
          },
          kubernetesConfigShortcut: {
            kubernetesAffinityShortcut: affinityFromSelector === 'none' ? '' : affinityFromSelector || '',
            kubernetesTolerationShortcut: tolerationFromSelector === 'none' ? '' : tolerationFromSelector || '',
          },
          runtimeEnvironmentVariables: deploymentOptions.envVariables,
        };

        // TODO: Remove column?.column and aggregation?.column checks when this issue is fixed - https://github.com/h2oai/mlops-model-ingest/issues/425.
        // It's due to "name" prop being empty in output schema - "output_schema": {"jsonValue": "[{\"name\": \"\", \"type\": \"Float32\"}]"}
        const enabledColumns = monitoringOptions?.columns?.filter(
          (column) => column?.column && !column?.isColumnDisabled
        );
        const enabledBaselineAggregations = monitoringOptions?.baselineAggregations?.filter(
          (aggregation) => aggregation?.column && enabledColumns?.some((column) => column.column === aggregation.column)
        );
        const requestBody: CreateDeploymentRequest = {
          parent: workspaceName,
          deployment: {
            displayName: deploymentName,
            description: deploymentDescription,
            monitoringOptions: !isMonitoringEnabled
              ? undefined
              : {
                  ...monitoringOptions,
                  enabled: isMonitoringEnabled,
                  columns: enabledColumns,
                  baselineAggregations: enabledBaselineAggregations,
                },
            single: deploymentType === 'single_model' ? primaryElement : undefined,
            split:
              deploymentType === 'ab_test'
                ? { splitElements: [primaryElement as SplitElement, secondaryElement as SplitElement] }
                : undefined,
            shadow:
              deploymentType === 'champion_challenger'
                ? ({ primaryElement, secondaryElement } as {
                    primaryElement: ShadowElement;
                    secondaryElement: ShadowElement;
                  })
                : undefined,
            security: {
              passphrase: hasHashAlgorithm
                ? {
                    passphrase,
                    hashAlgorithm: selectedSecurityOption?.hashAlgorithm as AuthenticationPassphrase_HashAlgorithm,
                  }
                : undefined,
              tokenAuth: hasAuthProtocol
                ? {
                    authenticationProtocol:
                      selectedSecurityOption?.authorizationProtocol as AuthorizationAccessToken_AuthorizationProtocolSecurityType,
                  }
                : undefined,
              disabledSecurity: selectedSecurityOptionKey === 'NO_SECURITY' ? {} : undefined,
            },
          },
        };

        await mlopsService.createDeployment({
          ...requestBody,
          deployment: { ...requestBody.deployment, displayName: deploymentName },
        });

        const message = `Deployment "${deploymentName}" created successfully.`;
        addToast({
          messageBarType: MessageBarType.success,
          message,
        });
        history.push(`/mlops/${workspaceName}${ROUTES.DEPLOYMENTS}`);
      } catch (err) {
        const message = `Failed to create deployment: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      } finally {
        loadStateRef.current.createDeployment = false;
        evaluateLoading();
        setIsSubmitDisabled(false);
      }
    }, [
      workspaceName,
      addToast,
      deploymentOptions,
      deploymentName,
      deploymentDescription,
      isMonitoringEnabled,
      monitoringOptions,
      deploymentType,
      passphrase,
      selectedSecurityOptionKey,
      selectedSecurityOption,
      hasHashAlgorithm,
      hasAuthProtocol,
      trafficDistribution,
      affinityFromSelector,
      tolerationFromSelector,
    ]),
    updateDeployment = React.useCallback(async () => {
      if (!workspaceName || !deploymentOptions) {
        return;
      }
      loadStateRef.current.updateDeployment = true;
      setLoading(true);
      setIsSubmitDisabled(true);
      try {
        const primaryElement = {
          // TODO: Unify deploymentCompositions.
          deploymentComposition: deploymentOptions?.firstModelDeploymentComposition || defaultDeploymentCompositionA,
          weight: deploymentType === 'ab_test' ? trafficDistribution : undefined,
          kubernetesResourceSpec: {
            kubernetesResourceRequirements: {
              requests:
                deploymentOptions?.requests || defaultKubernetesResourceSpec?.kubernetesResourceRequirements?.requests,
              limits:
                deploymentOptions?.limits || defaultKubernetesResourceSpec?.kubernetesResourceRequirements?.limits,
            },
            replicas:
              deploymentOptions?.kubernetesOptions?.replicas !== undefined
                ? deploymentOptions?.kubernetesOptions?.replicas
                : defaultKubernetesResourceSpec?.replicas,
          },
          kubernetesConfigShortcut: {
            kubernetesAffinityShortcut: affinityFromSelector === 'none' ? '' : affinityFromSelector || '',
            kubernetesTolerationShortcut: tolerationFromSelector === 'none' ? '' : tolerationFromSelector || '',
          },
          runtimeEnvironmentVariables: deploymentOptions?.envVariables || defaultEnvVariablesConfig,
        };

        const secondaryElement = {
          // TODO: Unify deploymentCompositions.
          deploymentComposition: deploymentOptions?.secondModelDeploymentComposition || defaultDeploymentCompositionB,
          weight: deploymentType === 'ab_test' ? MAX_TRAFFIC_WIGHT - trafficDistribution : undefined,
          kubernetesResourceSpec: {
            kubernetesResourceRequirements: {
              requests:
                deploymentOptions?.requests || defaultKubernetesResourceSpec?.kubernetesResourceRequirements?.requests,
              limits:
                deploymentOptions?.limits || defaultKubernetesResourceSpec?.kubernetesResourceRequirements?.limits,
            },
            replicas:
              deploymentOptions?.kubernetesOptions?.replicas !== undefined
                ? deploymentOptions?.kubernetesOptions?.replicas
                : defaultKubernetesResourceSpec?.replicas,
          },
          kubernetesConfigShortcut: {
            kubernetesAffinityShortcut: affinityFromSelector === 'none' ? '' : affinityFromSelector || '',
            kubernetesTolerationShortcut: tolerationFromSelector === 'none' ? '' : tolerationFromSelector || '',
          },
          runtimeEnvironmentVariables: deploymentOptions?.envVariables || defaultEnvVariablesConfig,
        };

        // TODO: Remove column?.column and aggregation?.column checks when this issue is fixed - https://github.com/h2oai/mlops-model-ingest/issues/425.
        // It's due to "name" prop being empty in output schema - "output_schema": {"jsonValue": "[{\"name\": \"\", \"type\": \"Float32\"}]"}
        const enabledColumns = monitoringOptions?.columns?.filter(
          (column) => column?.column && !column?.isColumnDisabled
        );
        const enabledBaselineAggregations = monitoringOptions?.baselineAggregations?.filter(
          (aggregation) => aggregation?.column && enabledColumns?.some((column) => column.column === aggregation.column)
        );

        const requestBody: UpdateDeploymentRequest = {
          updateMask,
          deployment: {
            // TODO: Add support for updating deployment name and description when supported by the API.
            name: deployment?.name,
            displayName: deploymentName,
            description: deploymentDescription,
            etag: deployment?.etag,
            createTime: deployment?.createTime,
            updateTime: deployment?.updateTime,
            creator: deployment?.creator,
            cors: deployment?.cors,
            monitoringOptions:
              !isMonitoringEnabledGlobally || (!deployment?.monitoringOptions && !isMonitoringEnabled)
                ? undefined
                : {
                    // Do not send all monitoring options on update as "baselineSource" and "timestampColumn" are currently not supported.
                    // ...monitoringOptions,
                    enabled: isMonitoringEnabled,
                    columns: enabledColumns,
                    baselineAggregations: isEmptyBaselineMonitoring ? [] : enabledBaselineAggregations,
                    kafkaTopic: monitoringOptions?.kafkaTopic ? monitoringOptions?.kafkaTopic : undefined,
                  },
            single: deploymentType === 'single_model' ? primaryElement : undefined,
            split:
              deploymentType === 'ab_test'
                ? { splitElements: [primaryElement as SplitElement, secondaryElement as SplitElement] }
                : undefined,
            shadow:
              deploymentType === 'champion_challenger'
                ? ({ primaryElement, secondaryElement } as {
                    primaryElement: ShadowElement;
                    secondaryElement: ShadowElement;
                  })
                : undefined,
            security: {
              passphrase: hasHashAlgorithm
                ? {
                    passphrase,
                    hashAlgorithm: selectedSecurityOption?.hashAlgorithm as AuthenticationPassphrase_HashAlgorithm,
                  }
                : undefined,
              tokenAuth: hasAuthProtocol
                ? {
                    authenticationProtocol:
                      selectedSecurityOption?.authorizationProtocol as AuthorizationAccessToken_AuthorizationProtocolSecurityType,
                  }
                : undefined,
              disabledSecurity: selectedSecurityOptionKey === 'NO_SECURITY' ? {} : undefined,
            },
          },
        };

        const response = await mlopsService.updateDeployment(requestBody);

        if (response && response.deployment) {
          const deploymentId = response.deployment.name;
          const message = `Deployment "${deploymentName}" (${deploymentId}) updated successfully.`;
          addToast({
            messageBarType: MessageBarType.success,
            message,
          });
          history.push(`/mlops/${workspaceName}${ROUTES.DEPLOYMENTS}`);
        } else {
          console.error('Failed to update deployment: No deployment information in response.');
        }
      } catch (err) {
        const message = `Failed to update deployment: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      } finally {
        loadStateRef.current.updateDeployment = false;
        evaluateLoading();
        setIsSubmitDisabled(false);
      }
    }, [
      workspaceName,
      addToast,
      deploymentOptions,
      deploymentName,
      deploymentDescription,
      isMonitoringEnabled,
      deployment?.name,
      deployment?.monitoringOptions,
      trafficDistribution,
      deploymentType,
      selectedSecurityOptionKey,
      selectedSecurityOption,
      hasHashAlgorithm,
      hasAuthProtocol,
      affinityFromSelector,
      tolerationFromSelector,
      defaultDeploymentCompositionA,
      defaultDeploymentCompositionB,
      defaultKubernetesResourceSpec?.replicas,
      defaultKubernetesResourceSpec?.kubernetesResourceRequirements?.requests,
      defaultKubernetesResourceSpec?.kubernetesResourceRequirements?.limits,
      defaultEnvVariablesConfig,
      monitoringOptions,
      updateMask,
      isEmptyBaselineMonitoring,
      passphrase,
    ]),
    getRegisteredModel = React.useCallback(
      async (modelId: string) => {
        try {
          const response = await registeredModelClient.getRegisteredModel({
            modelId,
          });
          return (response as GetRegisteredModelResponse).registeredModel;
        } catch (err) {
          const message = `Failed to fetch registered model: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        }
      },
      [addToast]
    ),
    pickModelVersionFromCurrenWorkspace = React.useCallback(
      async (modelVersions: RegisteredModelVersion[]) => {
        const versionsCount = modelVersions?.length || 0;
        if (versionsCount === 1) {
          return modelVersions?.[0];
        } else if (versionsCount > 1) {
          let version = 0;
          while (version < versionsCount) {
            const model = await getRegisteredModel(modelVersions?.[version]?.registeredModelId || '');
            if (`workspaces/${model?.projectId}` === workspaceName) {
              return modelVersions?.[version];
            }
            version++;
          }
          return undefined;
        } else {
          return undefined;
        }
      },
      [workspaceName]
    ),
    onDownloadLogs = React.useCallback(async () => {
      if (!deployment?.name) return;
      setIsGeneratingLogs(true);
      let logZipArtifactId: string | undefined = undefined;

      try {
        const generateResponse = await mlopsService.generateDeploymentLogs({ name: deployment.name });
        logZipArtifactId = generateResponse?.artifactId;

        if (!logZipArtifactId) {
          throw new Error('Log artifact ID not received from server.');
        }

        const fileName = `deployment-${deployment.name.replace('/', '_')}-logs.zip`;
        await downloadFileFromGoogleByteStream(logZipArtifactId, mlopsApiUrl, fileName);
      } catch (err) {
        const message = `Failed to generate and download logs: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
      } finally {
        setIsGeneratingLogs(false);
      }
    }, [deployment?.name, mlopsApiUrl, addToast]),
    onChangeMonitoringOptions = React.useCallback((options: MonitoringOptionsFixed) => {
      const enabledColumns = options?.columns?.filter((column) => !column?.isColumnDisabled);
      setNoMonitoringColumnsSpecified((enabledColumns || []).length === 0);
      setMonitoringOptions(options);
    }, []);

  React.useEffect(() => {
    if (isNew) getDefaultSecurityOption();
    getSecurityOptions();
  }, [isNew, getDefaultSecurityOption, getSecurityOptions]);

  React.useEffect(() => {
    if (
      defaultDeployment &&
      isExistingMonitoring &&
      baselineAggregates &&
      (!monitoringOptions?.baselineAggregations || monitoringOptions?.baselineAggregations.length === 0)
    ) {
      setDeployment({
        ...defaultDeployment,
        monitoringOptions: {
          ...defaultDeployment.monitoringOptions,
          baselineAggregations: baselineAggregates as BaselineAggregationFixed[],
        },
      });
    }

    if (!isExistingMonitoring && defaultDeployment) {
      setDeployment(defaultDeployment);
    }
  }, [
    defaultDeployment,
    baselineAggregates,
    isNew,
    experimentId,
    monitoringOptions?.baselineAggregations,
    isExistingMonitoring,
  ]);

  React.useEffect(() => {
    if (!workspaceName) return;
    if (deployment) {
      setLoading(true);
      loadStateRef.current.fetchingExistingDeploymentDetails = true;

      setDeploymentName(deployment.displayName || '');
      setDeploymentType(getDeploymentType(deployment));
      setPassphrase(
        deployment.security &&
          `passphrase` in deployment.security &&
          securityDropdownOptions?.find(
            (option) => option.key === defaultSelectedSecurityOptionKey && option.data?.hashAlgorithm
          )
          ? deployment.security.passphrase!.passphrase
          : getRandomHash()
      );
      setDeploymentDescription(deployment.description || '');
      setIsMonitoringEnabled(deployment?.monitoringOptions?.enabled || false);
      setMonitoringOptions(deployment.monitoringOptions);
      if (COLUMN_UPDATE_SUPPORTED) {
        setNonUpdatableColumns(deployment.monitoringOptions?.columns?.map((col) => col.column) || []);
      }

      if (deployment?.single) {
        setDefaultDeploymentCompositionA(deployment.single?.deploymentComposition);
        setDefaultKubernetesResourceSpec(deployment.single.kubernetesResourceSpec);
        setDefaultEnvVariablesConfig(deployment.single?.runtimeEnvironmentVariables || {});
        setAffinityFromSelector(deployment.single?.kubernetesConfigShortcut?.kubernetesAffinityShortcut || 'none');
        setTolerationFromSelector(deployment.single?.kubernetesConfigShortcut?.kubernetesTolerationShortcut || 'none');

        registeredModelVersionClient
          .getModelVersionForExperiment(
            new GetModelVersionForExperimentRequest({
              experimentId: deployment.single?.deploymentComposition?.experimentId || '',
            })
          )
          .then(async (response) => {
            const modelVersion = await pickModelVersionFromCurrenWorkspace(
              (response as GetModelVersionForExperimentResponse)?.registeredModelVersion || []
            );
            if (modelVersion) {
              setDefaultSelectedModelIds([modelVersion.registeredModelId || '']);
              setDefaultSelectedExperimentIds([modelVersion.experimentId || '']);
            }
          })
          .catch((err) => {
            const message = `Failed to fetch model version for experiment: ${formatError(err)}`;
            console.error(message);
            addToast({
              messageBarType: MessageBarType.error,
              message,
            });
          });
      }
      if (deployment.split) {
        setDefaultDeploymentCompositionA(deployment.split?.splitElements[0].deploymentComposition);
        setDefaultDeploymentCompositionB(deployment.split?.splitElements[1].deploymentComposition);
        setTrafficDistribution(
          (isMlopsApiV1_0_x
            ? deployment.split?.splitElements[0].weight
            : deployment.split?.splitElements[0].effectivePercentage) || DEFAULT_TRAFFIC_WEIGHT
        );
        setDefaultKubernetesResourceSpec(
          deployment.split?.splitElements[0].kubernetesResourceSpec as KubernetesResourceSpec
        );
        setDefaultEnvVariablesConfig(deployment.split?.splitElements[0].runtimeEnvironmentVariables || {});
        setAffinityFromSelector(
          deployment.split?.splitElements[0].kubernetesConfigShortcut?.kubernetesAffinityShortcut || 'none'
        );
        setTolerationFromSelector(
          deployment.split?.splitElements[0].kubernetesConfigShortcut?.kubernetesTolerationShortcut || 'none'
        );

        const firstRequest = registeredModelVersionClient
          .getModelVersionForExperiment(
            new GetModelVersionForExperimentRequest({
              experimentId: deployment.split?.splitElements[0].deploymentComposition?.experimentId || '',
            })
          )
          .then(async (response) => {
            const modelVersion = await pickModelVersionFromCurrenWorkspace(
              (response as GetModelVersionForExperimentResponse)?.registeredModelVersion || []
            );
            return modelVersion;
          })
          .catch((err) => {
            const message = `Failed to fetch model version for experiment: ${formatError(err)}`;
            console.error(message);
            addToast({
              messageBarType: MessageBarType.error,
              message,
            });
          });

        const secondRequest = registeredModelVersionClient
          .getModelVersionForExperiment(
            new GetModelVersionForExperimentRequest({
              experimentId: deployment.split.splitElements[1].deploymentComposition?.experimentId || '',
            })
          )
          .then(async (response) => {
            const modelVersion = await pickModelVersionFromCurrenWorkspace(
              (response as GetModelVersionForExperimentResponse)?.registeredModelVersion || []
            );
            return modelVersion;
          })
          .catch((err) => {
            const message = `Failed to fetch second model version for experiment: ${formatError(err)}`;
            console.error(message);
            addToast({
              messageBarType: MessageBarType.error,
              message,
            });
          });

        Promise.all([firstRequest, secondRequest]).then(([firstModelVersion, secondModelVersion]) => {
          setDefaultSelectedModelIds([
            firstModelVersion?.registeredModelId || '',
            secondModelVersion?.registeredModelId || '',
          ]);
          setDefaultSelectedExperimentIds([
            firstModelVersion?.experimentId || '',
            secondModelVersion?.experimentId || '',
          ]);
        });
      }

      if (deployment.shadow) {
        setDefaultDeploymentCompositionA(deployment.shadow.primaryElement?.deploymentComposition);
        setDefaultDeploymentCompositionB(deployment.shadow.secondaryElement?.deploymentComposition);
        setDefaultKubernetesResourceSpec(
          deployment.shadow?.primaryElement?.kubernetesResourceSpec as KubernetesResourceSpec
        );
        setDefaultEnvVariablesConfig(deployment.shadow?.primaryElement?.runtimeEnvironmentVariables || {});
        setAffinityFromSelector(
          deployment.shadow?.primaryElement?.kubernetesConfigShortcut?.kubernetesAffinityShortcut || 'none'
        );
        setTolerationFromSelector(
          deployment.shadow?.primaryElement?.kubernetesConfigShortcut?.kubernetesTolerationShortcut || 'none'
        );

        const primaryRequest = registeredModelVersionClient
          .getModelVersionForExperiment(
            new GetModelVersionForExperimentRequest({
              experimentId: deployment.shadow.primaryElement?.deploymentComposition?.experimentId || '',
            })
          )
          .then(async (response) => {
            const modelVersion = await pickModelVersionFromCurrenWorkspace(
              (response as GetModelVersionForExperimentResponse)?.registeredModelVersion || []
            );
            return modelVersion;
          })
          .catch((err) => {
            const message = `Failed to fetch model version for experiment: ${formatError(err)}`;
            console.error(message);
            addToast({
              messageBarType: MessageBarType.error,
              message,
            });
          });

        const secondaryRequest = registeredModelVersionClient
          .getModelVersionForExperiment(
            new GetModelVersionForExperimentRequest({
              experimentId: deployment.shadow.secondaryElement?.deploymentComposition?.experimentId || '',
            })
          )
          .then(async (response) => {
            const modelVersion = await pickModelVersionFromCurrenWorkspace(
              (response as GetModelVersionForExperimentResponse)?.registeredModelVersion || []
            );
            return modelVersion;
          })
          .catch((err) => {
            const message = `Failed to fetch second model version for experiment: ${formatError(err)}`;
            console.error(message);
            addToast({
              messageBarType: MessageBarType.error,
              message,
            });
          });

        Promise.all([primaryRequest, secondaryRequest]).then(([firstModelVersion, secondModelVersion]) => {
          setDefaultSelectedModelIds([
            firstModelVersion?.registeredModelId || '',
            secondModelVersion?.registeredModelId || '',
          ]);
          setDefaultSelectedExperimentIds([
            firstModelVersion?.experimentId || '',
            secondModelVersion?.experimentId || '',
          ]);
        });
      }

      loadStateRef.current.fetchingExistingDeploymentDetails = false;
      evaluateLoading();
    }
  }, [deployment, workspaceName, defaultSelectedSecurityOptionKey, securityDropdownOptions]);

  React.useEffect(() => {
    if (defaultSelectedSecurityOptionKey) {
      setSelectedSecurityOptionKey(defaultSelectedSecurityOptionKey);
      setSelectedSecurityOption(
        securityDropdownOptions?.find((option) => option.key === defaultSelectedSecurityOptionKey)?.data
      );
    }
  }, [defaultSelectedSecurityOptionKey, securityDropdownOptions]);

  React.useEffect(() => {
    // Disables submit button if deployment options are not set.
    if (!deploymentType) return;
    if (deploymentType === 'single_model') {
      // TODO: Unify deploymentCompositions.
      setIsSubmitDisabled(!(deploymentOptions?.firstModelDeploymentComposition || defaultDeploymentCompositionA));
    } else {
      setIsSubmitDisabled(
        !(deploymentOptions?.firstModelDeploymentComposition || defaultDeploymentCompositionA) ||
          !(deploymentOptions?.secondModelDeploymentComposition || defaultDeploymentCompositionB)
      );
    }
  }, [
    deploymentType,
    deploymentOptions?.firstModelDeploymentComposition,
    deploymentOptions?.secondModelDeploymentComposition,
    defaultDeploymentCompositionA,
    defaultDeploymentCompositionB,
  ]);

  React.useEffect(() => {
    // Collapses advanced settings when model is unselected.
    if (!selectedModels?.[0]) {
      setAdvancedMonitoringExpanded(false);
    }
  }, [selectedModels]);

  // Checks whether user changed deployment options.
  React.useEffect(() => {
    if (isNew || !deployment) return;
    // TODO: Optimize this to avoid expensive isEqual checks.
    const updateMask = [];

    const hasDisplayNameChanged = (deployment?.displayName || '') !== (deploymentName || '');
    if (hasDisplayNameChanged) {
      updateMask.push('display_name');
    }

    const hasDescriptionChanged = (deployment?.description || '') !== (deploymentDescription || '');
    if (hasDescriptionChanged) {
      updateMask.push('description');
    }

    const hasKubernetesOptionsChanged =
      // deploymentOptions are empty before being set by ModelSelector component.
      (deploymentOptions?.kubernetesOptions &&
        !isEqual(deploymentOptions?.kubernetesOptions?.replicas, defaultKubernetesResourceSpec?.replicas)) ||
      (deploymentOptions?.limits &&
        !isEqual(deploymentOptions?.limits, defaultKubernetesResourceSpec?.kubernetesResourceRequirements?.limits)) ||
      (deploymentOptions?.requests &&
        !isEqual(deploymentOptions?.requests, defaultKubernetesResourceSpec?.kubernetesResourceRequirements?.requests));
    if (hasKubernetesOptionsChanged) {
      updateMask.push('deployment.kubernetes_resource_spec');
    }

    const currentAffinity =
      deployment?.single?.kubernetesConfigShortcut?.kubernetesAffinityShortcut ||
      deployment?.split?.splitElements[0]?.kubernetesConfigShortcut?.kubernetesAffinityShortcut ||
      deployment?.shadow?.primaryElement?.kubernetesConfigShortcut?.kubernetesAffinityShortcut ||
      'none';
    const currentToleration =
      deployment?.single?.kubernetesConfigShortcut?.kubernetesTolerationShortcut ||
      deployment?.split?.splitElements[0]?.kubernetesConfigShortcut?.kubernetesTolerationShortcut ||
      deployment?.shadow?.primaryElement?.kubernetesConfigShortcut?.kubernetesTolerationShortcut ||
      'none';

    const hasKubernetesConfigShortcutChanged =
      currentAffinity !== affinityFromSelector || currentToleration !== tolerationFromSelector;
    if (hasKubernetesConfigShortcutChanged) {
      updateMask.push('deployment.kubernetes_config_shortcut');
    }

    const hasEnvVariablesChanged = !isEqual(
      deploymentOptions?.envVariables || {},
      deployment?.single?.runtimeEnvironmentVariables ||
        deployment?.split?.splitElements[0]?.runtimeEnvironmentVariables ||
        deployment?.shadow?.primaryElement?.runtimeEnvironmentVariables ||
        {}
    );
    if (hasEnvVariablesChanged) {
      updateMask.push('deployment.environment_variables');
    }

    const hasSecurityChanged =
      (deployment.security &&
        `passphrase` in deployment.security &&
        deployment.security.passphrase?.passphrase !== passphrase) ||
      defaultSelectedSecurityOptionKey !== selectedSecurityOptionKey;
    if (hasSecurityChanged) {
      updateMask.push('deployment.security');
    }

    const enabledColumns = monitoringOptions?.columns?.filter((column) => !column?.isColumnDisabled),
      enabledBaselineAggregations = monitoringOptions?.baselineAggregations?.filter((aggregation) =>
        enabledColumns?.some((column) => column.column === aggregation.column)
      ),
      oldMonitoringOptions = {
        enabled: !!deployment?.monitoringOptions?.enabled,
        columns: deployment?.monitoringOptions?.columns,
        baselineAggregations: (deployment?.monitoringOptions?.baselineAggregations as BaselineAggregationFixed[])?.map(
          (aggregation) => ({
            column: aggregation.column,
            logicalType: aggregation.logicalType,
            ...(aggregation.categoricalAggregate ? { categoricalAggregate: aggregation.categoricalAggregate } : {}),
            ...(aggregation.numericalAggregate ? { numericalAggregate: aggregation.numericalAggregate } : {}),
          })
        ),
        kafkaTopic: deployment?.monitoringOptions?.kafkaTopic ? deployment?.monitoringOptions?.kafkaTopic : undefined,
      },
      newMonitoringOptions = {
        enabled: isMonitoringEnabled,
        columns: enabledColumns?.length ? enabledColumns : undefined,
        baselineAggregations: enabledBaselineAggregations?.length
          ? enabledBaselineAggregations.map((aggregation) => ({
              column: aggregation.column,
              logicalType: aggregation.logicalType,
              ...(aggregation.categoricalAggregate ? { categoricalAggregate: aggregation.categoricalAggregate } : {}),
              ...(aggregation.numericalAggregate ? { numericalAggregate: aggregation.numericalAggregate } : {}),
            }))
          : undefined,
        kafkaTopic: monitoringOptions?.kafkaTopic ? monitoringOptions?.kafkaTopic : undefined,
      },
      hasMonitoringOptionsChanged = !isEqual(oldMonitoringOptions, newMonitoringOptions);
    if (hasMonitoringOptionsChanged) {
      updateMask.push('deployment.monitoring');
    }

    const hasSomethingChanged =
      hasDisplayNameChanged ||
      hasDescriptionChanged ||
      hasKubernetesOptionsChanged ||
      hasKubernetesConfigShortcutChanged ||
      hasEnvVariablesChanged ||
      hasSecurityChanged ||
      hasMonitoringOptionsChanged;

    if (hasSomethingChanged) {
      setUpdateMask(updateMask.join(','));
    } else {
      setUpdateMask(undefined);
    }
  }, [
    deployment,
    isNew,
    deploymentOptions?.kubernetesOptions?.replicas,
    deploymentOptions?.limits,
    deploymentOptions?.requests,
    deploymentOptions?.envVariables,
    deploymentName,
    deploymentDescription,
    affinityFromSelector,
    tolerationFromSelector,
    passphrase,
    defaultSelectedSecurityOptionKey,
    selectedSecurityOptionKey,
    monitoringOptions,
    isMonitoringEnabled,
    defaultKubernetesResourceSpec,
    defaultEnvVariablesConfig,
  ]);

  React.useEffect(() => {
    setSecurityDropdownOptions(getSecurityDropdownOptions(securityOptions, defaultSelectedSecurityOptionKey));
  }, [securityOptions, defaultSelectedSecurityOptionKey]);

  const { handlePauseDeployment, handleResumeDeployment } = usePauseResumeDeployment();
  const mlopsApiVersionQuery = useQueryMlopsApiVersion();
  const mlopsApiVersion = mlopsApiVersionQuery.data || FALLBACK_MLOPS_API_VERSION;
  const isMlopsApiV1_0_x = semver.satisfies(mlopsApiVersion, '1.0.x');
  const listRowColumns = useListRowColumns({
    onDeleteDeployment: handleDeleteDeployment,
    onPauseDeployment: handlePauseDeployment,
    onResumeDeployment: handleResumeDeployment,
    onRetryDeployment: (deploymentName) => {
      retryDeploymentMutation.mutate({
        name: deploymentName,
      });
    },
    permissions,
    isMlopsApiV1_0_x: Boolean(isMlopsApiV1_0_x),
  });
  const { deployment: deploymentId, workspace: workspaceIdFromDeployment } = React.useMemo(() => {
    if (!deployment?.name) return { deployment: ``, workspace: `` };
    return deploymentNameCompileParse.parse(deployment.name);
  }, [deployment?.name]);
  const updatingOnlyNameOrDescription =
    updateMask === 'display_name' || updateMask === 'description' || updateMask === 'display_name,description';

  return (
    <>
      <div className={classNames.form}>
        {deploymentStatus?.state ? (
          <>
            <ListRow
              columns={listRowColumns}
              contents={
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <b>Status detail: </b>
                    {deploymentStatus?.message || 'No details available.'}
                  </div>
                  <div style={{ height: 6 }}></div>
                  <Button
                    iconName={isGeneratingLogs ? 'Loader' : 'DownloadDocument'}
                    styles={buttonStylesStealth}
                    style={{ marginRight: 13 }}
                    loading={isGeneratingLogs}
                    disabled={!deployment?.name}
                    onClick={onDownloadLogs}
                  >
                    Download logs
                  </Button>
                </div>
              }
              data={
                {
                  ...deployment,
                  actionsDisabled: widgetRowActionsDisabled,
                  status: deploymentStatus,
                  createdByName: deploymentDetailProps.createdBy,
                  permissions,
                  onDeleteDeployment: () => {
                    if (deployment?.name) {
                      handleDeleteDeployment(deployment.name);
                    }
                  },
                  onRetryDeployment: () => {
                    setWidgetRowActionsDisabled(true);
                    retryDeploymentMutation.mutate({
                      name: deployment?.name || '',
                    });
                  },
                  id: deploymentId,
                  workspaceId: workspaceIdFromDeployment,
                } as DeploymentItemData
              }
              styles={getListRowStyles(theme)}
              style={{ maxWidth: FORM_SECTION_MAX_WIDTH }}
            />
            <div style={{ height: 25 }}></div>
          </>
        ) : null}
        <FormSection title="General settings">
          <TextField
            label="Deployment name"
            required
            onChange={(_ev, value) => {
              setDeploymentName(value || '');
            }}
            value={deploymentName}
            disabled={!(permissions?.isAbleToCreateDeployments || permissions?.isAbleToUpdateDeployments)}
            styles={{ root: { maxWidth: 360 } }}
          />
          <TextField
            label="Deployment description"
            onChange={(_ev, value) => {
              setDeploymentDescription(value || '');
            }}
            value={deploymentDescription}
            disabled={!(permissions?.isAbleToCreateDeployments || permissions?.isAbleToUpdateDeployments)}
            styles={{ root: { maxWidth: 360 } }}
          />
          <Dropdown
            label="Deployment type"
            selectedKey={deploymentType}
            onChange={onChangeDeploymentType}
            options={dropdownOptions}
            disabled={
              !updateDeleteAllowed ||
              !(permissions?.isAbleToCreateDeployments || permissions?.isAbleToUpdateDeployments) ||
              !isNew
            }
            styles={{ root: { maxWidth: 360 } }}
          />
        </FormSection>
        <FormSection title={`Model${deploymentType !== 'single_model' ? 's' : ''}`}>
          <ModelSelector
            selectedModels={selectedModels}
            onSelectedModelsChange={setSelectedModels}
            defaultSelectedVersionId={deployModelVersionId}
            defaultSelectedModelIds={defaultSelectedModelIds}
            defaultSelectedExperimentIds={defaultSelectedExperimentIds}
            defaultDeploymentCompositionA={defaultDeploymentCompositionA}
            defaultDeploymentCompositionB={defaultDeploymentCompositionB}
            defaultLimitsConfig={defaultKubernetesResourceSpec?.kubernetesResourceRequirements?.limits}
            defaultRequestsConfig={defaultKubernetesResourceSpec?.kubernetesResourceRequirements?.requests}
            defaultReplicas={defaultKubernetesResourceSpec?.replicas}
            defaultEnvVariablesConfig={defaultEnvVariablesConfig}
            trafficDistribution={trafficDistribution}
            onTrafficDistributionChange={onChangeTrafficDistribution}
            defaultAffinity={
              deployment?.single?.kubernetesConfigShortcut?.kubernetesAffinityShortcut ||
              deployment?.split?.splitElements[0]?.kubernetesConfigShortcut?.kubernetesAffinityShortcut ||
              deployment?.shadow?.primaryElement?.kubernetesConfigShortcut?.kubernetesAffinityShortcut ||
              'none'
            }
            defaultToleration={
              deployment?.single?.kubernetesConfigShortcut?.kubernetesTolerationShortcut ||
              deployment?.split?.splitElements[0]?.kubernetesConfigShortcut?.kubernetesTolerationShortcut ||
              deployment?.shadow?.primaryElement?.kubernetesConfigShortcut?.kubernetesTolerationShortcut ||
              'none'
            }
            onAffinityChange={setAffinityFromSelector}
            onTolerationChange={setTolerationFromSelector}
            deploymentType={deploymentType}
            onChangeOptions={setDeploymentOptions}
            onContentLoadStart={() => setIsWidgetLoading(true)}
            onContentLoadFinish={() => setIsWidgetLoading(false)}
            readOnly={
              !(permissions?.isAbleToUpdateDeployments || permissions?.isAbleToCreateDeployments) ||
              !updateDeleteAllowed
            }
            isNew={isNew}
            workspaceId={workspaceId}
          />
        </FormSection>
        {areAllModelsSelected ? (
          <>
            {permissions.isAbleToViewDeploymentMonitoring || permissions.isAbleToSetDeploymentMonitoring ? (
              <>
                <FormSection title="Monitoring">
                  <Toggle
                    label="Enable monitoring"
                    onText="Yes"
                    offText="No"
                    checked={isMonitoringEnabled}
                    disabled={
                      !permissions.isAbleToSetDeploymentMonitoring ||
                      !updateDeleteAllowed ||
                      !(permissions?.isAbleToCreateDeployments || permissions?.isAbleToUpdateDeployments) ||
                      !isMonitoringEnabledGlobally
                    }
                    onChange={(_ev, checked) => {
                      setIsMonitoringEnabled(checked || false);
                    }}
                  />
                  {!isMonitoringEnabledGlobally && (
                    <Info style={{ marginTop: 10 }} styles={{ text: { alignContent: 'center' } }}>
                      Monitoring is not allowed in this environment. In case you need it, please contact our support
                      team.
                    </Info>
                  )}
                  <span
                    // Hides form istead of removing it to keep form data for submission when advanced settings are collapsed.
                    style={
                      isMonitoringEnabled
                        ? undefined
                        : { visibility: 'hidden', display: 'block', height: 0, overflow: 'hidden' }
                    }
                  >
                    <Expander
                      label={'Monitoring options'}
                      onChangeExpanded={(isExpanded) => setAdvancedMonitoringExpanded(isExpanded)}
                    />
                    <span
                      // Hides form istead of removing it to keep form data for submission when advanced settings are collapsed.
                      style={
                        isAdvancedMonitoringExpanded
                          ? undefined
                          : { visibility: 'hidden', height: 0, overflow: 'hidden', display: 'block' }
                      }
                    >
                      {experimentId ? (
                        <Monitoring
                          monitoringOptions={monitoringOptions}
                          onChangeMonitoringOptions={onChangeMonitoringOptions}
                          disabled={
                            !permissions.isAbleToSetDeploymentMonitoring ||
                            !(permissions?.isAbleToCreateDeployments || permissions?.isAbleToUpdateDeployments) ||
                            !updateDeleteAllowed ||
                            (COLUMN_UPDATE_SUPPORTED
                              ? false
                              : // Only allow update when columns were previously empty.
                                (deployment?.monitoringOptions?.columns || []).length !== 0)
                          }
                          experimentId={experimentId || ''}
                          nonUpdatableColumns={COLUMN_UPDATE_SUPPORTED ? nonUpdatableColumns : undefined}
                          isExistingMonitoring={isExistingMonitoring}
                          shouldFetchMetadata={
                            isMonitoringEnabled &&
                            (!isExistingMonitoring || (isExistingMonitoring && !baselineAggregatesQuery.isLoading))
                          }
                        />
                      ) : (
                        <Loader label="Obtaining scoring configurations for selected model..." />
                      )}
                    </span>
                    {isMonitoringEnabled && noMonitoringColumnsSpecified ? (
                      <Info
                        style={{ marginTop: 10 }}
                        styles={{
                          text: { alignContent: 'center', color: theme.semanticColors?.inputErrorMessageText },
                          icon: {
                            color: `${theme.semanticColors?.inputErrorMessageText} !important`,
                          },
                        }}
                      >
                        At least one column must be specified for monitoring.
                      </Info>
                    ) : null}
                  </span>
                </FormSection>
              </>
            ) : (
              <FormSection title="Monitoring">
                <Info style={{ marginTop: 10 }} styles={{ text: { alignContent: 'center' } }}>
                  You have no permissions to access monitoring in this workspace. Ask to workspace owner to grant you
                  the necessary permissions.
                </Info>
              </FormSection>
            )}
            <FormSection title="Security">
              <Dropdown
                label="Level"
                disabled={
                  !updateDeleteAllowed ||
                  !(permissions?.isAbleToCreateDeployments || permissions?.isAbleToUpdateDeployments)
                }
                options={securityDropdownOptions}
                selectedKey={selectedSecurityOptionKey}
                onChange={(_ev, item) => {
                  if (!isNew && !hasPassphraseChanged) setHasPassphraseChanged(true);
                  setSelectedSecurityOptionKey(item?.key as SecurityKey | undefined);
                  setSelectedSecurityOption(item?.data as SecurityOptionTypeFixed | undefined);
                  if (item?.key !== 'NO_SECURITY') setPassphrase(getRandomHash());
                }}
                styles={{ root: { maxWidth: 360 } }}
                required
              />
              {/*  Show passphrase only if hashAlgorithm is specified. */}
              {showHashInput && (
                <TextField
                  label="Passphrase"
                  readOnly={
                    !updateDeleteAllowed ||
                    !(permissions?.isAbleToCreateDeployments || permissions?.isAbleToUpdateDeployments)
                  }
                  type={!isNew ? 'password' : undefined}
                  canRevealPassword={!isNew}
                  value={passphrase}
                  onChange={(_ev, value) => {
                    setPassphrase(value || '');
                  }}
                  styles={{ root: { maxWidth: 360 } }}
                />
              )}
              {selectedSecurityOptionKey !== 'NO_SECURITY' &&
                !hasAuthProtocol &&
                (isNew || defaultSelectedSecurityOptionKey !== selectedSecurityOptionKey || hasPassphraseChanged) &&
                hasHashAlgorithm && (
                  <Info style={{ marginTop: 10 }} styles={{ text: { alignContent: 'center' } }}>
                    {isPlaintext
                      ? 'The passphrase is stored in plain text in the database. If you forget the passphrase, it will still be visible in the UI after deployment.'
                      : 'The passphrase is stored as a hashed value in the database. You must save this passphrase somewhere else for your future use. If you forgot the passphrase, it will not be visible in the UI after deployment.'}
                  </Info>
                )}
            </FormSection>
          </>
        ) : null}
        <div style={{ height: 25 }}></div>
        {permissions?.isAbleToCreateDeployments || permissions?.isAbleToUpdateDeployments ? (
          <Button
            // TODO: Add onCreateDeploymentClick function with form validation.
            onClick={isNew ? createDeployment : updateDeployment}
            // TODO: Handle disabled based on form validation.
            disabled={
              isWidgetLoading ||
              (!!deploymentOptions?.firstModelDeploymentComposition?.experimentId &&
                !!deploymentOptions?.secondModelDeploymentComposition?.experimentId &&
                deploymentOptions?.firstModelDeploymentComposition?.experimentId ===
                  deploymentOptions?.secondModelDeploymentComposition?.experimentId) ||
              (deploymentType === 'single_model'
                ? countSelectedModels(selectedModels) < 1
                : countSelectedModels(selectedModels) < 2) ||
              // Handles case where model has no version.
              // TODO: Unify deploymentCompositions.
              (deploymentType === 'single_model' &&
                !(
                  deploymentOptions?.firstModelDeploymentComposition?.experimentId ||
                  defaultDeploymentCompositionA?.experimentId
                )) ||
              (deploymentType !== 'single_model' &&
                !(
                  deploymentOptions?.firstModelDeploymentComposition?.experimentId ||
                  defaultDeploymentCompositionA?.experimentId
                )) ||
              (deploymentType !== 'single_model' &&
                !(
                  deploymentOptions?.secondModelDeploymentComposition?.experimentId ||
                  defaultDeploymentCompositionA?.experimentId
                )) ||
              // Update of displayName or description only is allowed in any state.
              (updatingOnlyNameOrDescription ? false : !updateDeleteAllowed) ||
              !deploymentName ||
              isSubmitDisabled ||
              (!isNew && !updateMask) ||
              // Cannot update with empty monitoring columns.
              (isMonitoringEnabled && noMonitoringColumnsSpecified)
            }
            styles={buttonStylesPrimary}
          >
            {isNew ? 'Deploy' : 'Save changes'}
          </Button>
        ) : null}
      </div>
    </>
  );
};
